import { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  TextareaAutosize,
  Typography,
  Autocomplete,
  Button,
  FormControlLabel,
  Dialog,
  DialogContent,
  Card,
  CardActions,
  CardHeader,
  CardContent,
  FormGroup,
  Checkbox,
} from "../../../../components";
import {
  GruposTributarioService,
  TiposItemService,
  OrigemMercadoriaService,
  notification,
  ProdutosService,
  SessionService,
} from "../../../../services";
import { responseErros, timeoutBuscaAutocomplete } from "../../../../utils";
import "./text-area.scss";
import { value } from "../../../../helpers";
import FormCombustivelDialog from "./formCombustivel.component";
import FormAnvisaDialog from "./formAnvisa.component";

const FormFiscalComponent = ({
  produto,
  setProduto,
  inputErros,
  setLoading,
  ncmList,
  buscarNcms,
  buscarCests,
  tabelaCest,
  loadingAutoComplete,
  setLoadingAutoComplete,
  handleChangeAutocomplete,
  handleInputChange,
  produtoEntity,
  listaUnidadesComerciais,
  openAnvisaDialog,
  setOpenAnvisaDialog,
  estados,
  gruposTributacao,
  setOpenAnaliseAIDialog,
}) => {
  const medicamentosNcm = ["3001", "3002", "3003", "3004", "3005", "3006"];

  const inputRef = useRef();
  const inputCestRef = useRef();
  const [origensMercadorias, setOrigensMercadorias] = useState([]);
  const [tiposItem, setTiposItem] = useState([]);
  const [autorizarConsultaConfisped, setAutorizarConsultaConfisped] =
    useState(false);
  const [confispedChanged, setConfispedChanged] = useState(false);
  const [openCombustivelDialog, setOpenCombustivelDialog] = useState(false);
  const produtosService = new ProdutosService();
  const gruposTributarioService = new GruposTributarioService();
  const tiposItemService = new TiposItemService();
  const origemMercadoriaService = new OrigemMercadoriaService();
  const sessionService = new SessionService();
  const empresa = sessionService.getEmpresa();

  useEffect(() => {
    buscarTiposItem();
    buscarOrigensMercadoria();
    setAutorizarConsultaConfisped(empresa.autorizarConsultaConfisped);
  }, []);

  async function revisarProdutoFiscal(body) {
    if (!body?.ean) {
      return notification.alertaGenericos(
        "Código de barras é obrigatório para realizar 'Consulta Fiscal'",
      );
    }
    setLoading(true);
    const result = await produtosService.getConfispedApi(body);
    if (!result.isAxiosError) {
      if (result.data.ncm) {
        const grupoT = await buscarCsoGrupoTributario(result.data.cso);
        const ncm = await buscarNcms({ codigo: result.data.ncm });
        produtoEntity.setProdutoNcm(ncm[0]);
        if (grupoT?.id) {
          produtoEntity.setProdutoGrupoTributacao(grupoT.id);
        }
        setProduto({ ...produtoEntity });
        notification.sucessoGenericos("Revisão realizada");
        setConfispedChanged(true);
      } else {
        notification.alertaGenericos(
          "Dados do produto não encontrados para consulta",
        );
      }
    } else {
      notification.alertaGenericos(
        "Dados do produto não encontrados para consulta",
      );
    }
    setLoading(false);
  }

  async function buscarCsoGrupoTributario(cso) {
    const res = await gruposTributarioService.getAll({
      csoSimplesNacional: cso,
    });
    if (!res.isAxiosError) {
      if (res.data) {
        return res.data.rows[0];
      }
    }
  }

  const buscarOrigensMercadoria = () => {
    origemMercadoriaService.getAll().then((res) => {
      if (!res.isAxiosError) {
        setOrigensMercadorias(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const buscarTiposItem = () => {
    tiposItemService.getAll().then((res) => {
      if (!res.isAxiosError) {
        setTiposItem(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const onInputChangeCest = (event) => {
    if (
      event.key === "Enter" &&
      inputCestRef.current === document.activeElement
    ) {
      const eventValue = event.target?.value;
      if (eventValue?.length >= 2) {
        const filtros = {
          codigo: eventValue,
        };
        setLoadingAutoComplete(true);
        timeoutBuscaAutocomplete(buscarCests(filtros));
      }
    }
  };

  const onInputChangeNcm = (event) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      if (eventValue?.length >= 2) {
        const filtros = {
          codigo: eventValue,
        };
        setLoadingAutoComplete(true);
        timeoutBuscaAutocomplete(
          Promise.all([
            buscarNcms(filtros),
            buscarCests({ ncm: eventValue.substring(0, 4) }),
          ]),
        );
      }
    }
  };

  const handleCheckboxChange = (type) => (event) => {
    const checked = event.target.checked;
    produtoEntity.setIsMedicamento(false);
    produtoEntity.setIsCombustivel(false);
    if (checked) {
      if (type === "medicamento") {
        produtoEntity.setIsMedicamento(true);
      } else if (type === "combustivel") {
        produtoEntity.setIsCombustivel(true);
      }
    }
    setProduto((prevState) => ({ ...prevState, ...produtoEntity }));
  };

  const handleChangeNcmAutocomplete = (value) => {
    if (medicamentosNcm.includes(value?.codigo?.slice(0, 4))) {
      produtoEntity.setIsMedicamento(true);
      produtoEntity.setIsCombustivel(false);
      setProduto((prevState) => ({ ...prevState, ...produtoEntity }));
    }
    return handleChangeAutocomplete("ncmId", value);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={autorizarConsultaConfisped ? 5 : 6}>
          <Autocomplete
            required
            id="origemMercadoriaId"
            name="origemMercadoriaId"
            options={origensMercadorias}
            noOptionsText="Sem opções"
            autoHighlight
            getOptionLabel={(option) =>
              option ? `${option?.codigo}-${option.descricao}` : ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Origem da Mercadoria"
                variant="outlined"
                required
                error={inputErros.get("origemMercadoriaId")}
              />
            )}
            onChange={(_, value) =>
              handleChangeAutocomplete("origemMercadoriaId", value)
            }
            value={value.autoComplete(
              origensMercadorias,
              produto.origemMercadoriaId,
            )}
          />
        </Grid>
        <Grid item xs={autorizarConsultaConfisped ? 5 : 6}>
          <Autocomplete
            id="tipoItemId"
            name="tipoItemId"
            options={tiposItem}
            noOptionsText="Sem opções"
            autoHighlight
            getOptionLabel={(option) => option.descricao ?? ""}
            renderInput={(params) => (
              <TextField {...params} label="Tipo do Item" variant="outlined" />
            )}
            onChange={(_, value) =>
              handleChangeAutocomplete("tipoItemId", value)
            }
            value={value.autoComplete(tiposItem, produto.tipoItemId)}
          />
        </Grid>
        {autorizarConsultaConfisped && (
          <Grid item xs={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="small"
              sx={{
                height: "50px",
                minWidth: "15px",
                boxShadow: "none",
              }}
              onClick={() =>
                revisarProdutoFiscal({
                  ean: produto.codigoBarra,
                  descricao: produto.descricao,
                })
              }
            >
              Revisão Tributária
            </Button>
          </Grid>
        )}
        <Grid item xs={3}>
          <Autocomplete
            id="grupoTributacaoId"
            name="grupoTributacaoId"
            options={gruposTributacao}
            noOptionsText="Sem opções"
            autoHighlight
            disabled={produto?.envioKitItensFiscal}
            disableClearable
            getOptionLabel={(option) => option.nome ?? ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Grupo Tributário"
                variant="outlined"
                required
                color={confispedChanged && "success"}
                focused={confispedChanged}
                error={inputErros.get("grupoTributacaoId")}
              />
            )}
            onChange={(_, value) =>
              handleChangeAutocomplete("grupoTributacaoId", value)
            }
            value={value.autoComplete(
              gruposTributacao,
              produto.grupoTributacaoId,
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Autocomplete
            id="ncmId"
            name="ncmId"
            options={ncmList}
            autoHighlight
            loading={loadingAutoComplete}
            loadingText="Carregando"
            noOptionsText="Digite e pressione Enter"
            getOptionLabel={(option) => option?.codigo ?? ""}
            disabled={produto?.envioKitItensFiscal}
            filterSelectedOptions
            onChange={(_, value) => handleChangeNcmAutocomplete(value)}
            value={value.autoComplete(ncmList, produto.ncmId)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="NCM"
                onKeyDown={(e) => onInputChangeNcm(e)}
                inputRef={inputRef}
                color={confispedChanged && "success"}
                error={inputErros.get("ncmId")}
                focused={confispedChanged}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Autocomplete
            id="cestId"
            name="cestId"
            options={tabelaCest}
            loadingText="Carregando"
            noOptionsText="Digite e pressione Enter"
            autoHighlight
            getOptionLabel={(option) => option?.codigo ?? ""}
            disabled={produto?.envioKitItensFiscal}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="CEST"
                onKeyDown={(e) => onInputChangeCest(e)}
                inputRef={inputCestRef}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
            filterSelectedOptions
            filterOptions={(options, state) =>
              options.filter(
                (item) =>
                  item?.codigo?.includes(state.inputValue) ||
                  item?.ncmId?.includes(produto.ncmId),
              )
            }
            onChange={(_, value) => handleChangeAutocomplete("cestId", value)}
            value={value.autoComplete(tabelaCest, produto.cestId)}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            id="gtin"
            name="gtin"
            label="N° Global Item"
            variant="outlined"
            error={inputErros.get("gtin")}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: { min: 0, maxLength: 13 },
            }}
            value={produto?.gtin ?? ""}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={3.5}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={produto?.isMedicamento || false}
                  onChange={handleCheckboxChange("medicamento")}
                  size="small"
                  disabled={medicamentosNcm.includes(
                    produto?.ncmCodigo?.slice(0, 4),
                  )}
                />
              }
              label="É um medicamento?"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={produto?.isCombustivel || false}
                  onChange={handleCheckboxChange("combustivel")}
                  size="small"
                  disabled={medicamentosNcm.includes(
                    produto?.ncmCodigo?.slice(0, 4),
                  )}
                />
              }
              label="É um combustível?"
            />
          </FormGroup>
        </Grid>
        {(() => {
          if (produto?.isMedicamento) {
            return (
              <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="small"
                  sx={{
                    height: "50px",
                    minWidth: "15px",
                    boxShadow: "none",
                  }}
                  onClick={() => setOpenAnvisaDialog(true)}
                >
                  Informações ANVISA
                </Button>
              </Grid>
            );
          }
          if (produto?.isCombustivel) {
            return (
              <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="small"
                  sx={{
                    height: "50px",
                    minWidth: "15px",
                    boxShadow: "none",
                  }}
                  onClick={() => setOpenCombustivelDialog(true)}
                >
                  Informações Combustível
                </Button>
              </Grid>
            );
          }
        })()}
        {empresa?.moduloIAfiscal && (
          <Grid
            item
            xs={produto?.isMedicamento || produto?.isCombustivel ? 6.5 : 8.5}
            className="d-flex justify-content-end"
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              disableElevation
              sx={{
                px: 3,
                height: "50px",
                minWidth: "15px",
                boxShadow: "none",
              }}
              onClick={() => {
                if (produto?.descricao) {
                  setOpenAnaliseAIDialog(true);
                  return;
                }
                notification.alertaGenericos(
                  "A descrição do produto é obrigatória para análise fiscal",
                );
              }}
            >
              <i
                className="ph-fill ph-sparkle"
                style={{ fontSize: 18, marginRight: 15, color: "#fff" }}
              ></i>
              Inteligência Fiscal
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ marginTop: "10px" }}>
        <Grid item xs={6}>
          <Typography className="text-start">
            Informação Adicional Nfe
          </Typography>
          <TextareaAutosize
            className="textRolagem"
            id="informacaoAdicionalNfe"
            name="informacaoAdicionalNfe"
            label="Informação Adicional Nfe"
            variant="outlined"
            maxLength={255}
            value={produto.informacaoAdicionalNfe ?? ""}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography className="text-start">Observação</Typography>
          <TextareaAutosize
            className="textRolagem"
            id="informacaoComplementar"
            name="informacaoComplementar"
            label="Info. Complementar"
            variant="outlined"
            maxLength={255}
            value={produto.informacaoComplementar ?? ""}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>
      <Dialog
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        PaperComponent="div"
        open={openAnvisaDialog}
        aria-labelledby="form-dialog-title"
      >
        <DialogContent>
          <Card className="p-3">
            <CardHeader title="Informações ANVISA" />
            <CardContent className="p-3">
              <FormAnvisaDialog
                produto={produto}
                setProduto={setProduto}
                produtoEntity={produtoEntity}
                inputErros={inputErros}
              />
            </CardContent>
            <CardActions>
              <Button
                color="inherit"
                variant="contained"
                onClick={() => setOpenAnvisaDialog(false)}
              >
                Fechar
              </Button>
            </CardActions>
          </Card>
        </DialogContent>
      </Dialog>
      <Dialog
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        PaperComponent="div"
        open={openCombustivelDialog}
        aria-labelledby="form-dialog-title"
      >
        <DialogContent>
          <Card className="p-3">
            <CardHeader title="Informações de Combustível" />
            <CardContent className="p-3">
              <FormCombustivelDialog
                produto={produto}
                setProduto={setProduto}
                produtoEntity={produtoEntity}
                inputErros={inputErros}
                loadingAutoComplete={loadingAutoComplete}
                setLoadingAutoComplete={setLoadingAutoComplete}
                handleChangeAutocomplete={handleChangeAutocomplete}
                listaUnidadesComerciais={listaUnidadesComerciais}
                estados={estados}
              />
            </CardContent>
            <CardActions>
              <Button
                color="inherit"
                variant="contained"
                onClick={() => setOpenCombustivelDialog(false)}
                sx={{ my: -2 }}
              >
                Fechar
              </Button>
            </CardActions>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FormFiscalComponent;
